/**
 * „Neue Version“ geklickt, aber die Seite kommt mit der alten Fassung zurück: Schutz gegen die Schleife. (29.10.2026)
 *
 * Der Klick merkt sich im sessionStorage (überlebt das Neuladen) die Fassung, die der Server meldete. Zeigt die neu geladene
 * Seite danach immer noch eine ältere als diese, gibt es GENAU EINEN automatischen Versuch mit hartem Neuladen (Worker
 * abmelden, Speicher leeren, neu laden). Hilft auch das nicht, steht eine klare Zeile da und es lädt nichts mehr von selbst;
 * der nächste Druck auf den Knopf geht dann direkt den harten Weg.
 *
 * ⚠️ Nur dieser Weg lädt hart. Er ändert nichts an den Regeln aus lib/app-neu-laden.ts (nicht mitten in Aufnahme/Antwort):
 *    der Aufrufer fragt vorher `neuLadenDarfJetzt`.
 */

export const VERSUCH_MERKER = "pawbot:neueVersionVersuch";
/** Ein Merker älter als das gilt nicht mehr. */
export const VERSUCH_GUELTIG_MS = 10 * 60_000;

export interface Versuch {
  /** Die Fassung des Servers (roh), die der Klick laden wollte. */
  ziel: string;
  /** 1 = geklickt, 2 = automatischer harter Versuch läuft, 3 = gemeldet. */
  stufe: 1 | 2 | 3;
  zeit: number;
}

export type NachLaden = "nichts" | "hart" | "melden";

/** Der Zeitstempel eines Baus („8.0.0-pre.4010-1788000000000“ → 1788000000000), sonst null. */
export function bauZeit(roh: string): number | null {
  const m = /-(\d{13})$/.exec(roh.trim());
  return m ? Number(m[1]) : null;
}

/**
 * Hat der SERVER eine neuere Fassung als die geladene Seite? (29.10.2026)
 * Nur „neuer“ zählt: der Dienst liefert build-version.json aus dem Stand beim Start (server/boot-fassung.ts), die Seiten kommen
 * live von der Platte. Eine Seite, die nach einem Bau (vor dem Neustart) geladen wurde, ist NEUER als der Dienst — das ist kein Update.
 * Ohne lesbare Bauzeit auf beiden Seiten (alte Form, Tests) gilt wie früher: ungleich = neu.
 */
export function serverIstNeuer(laufend: string, server: string | null): boolean {
  if (!laufend || !server || laufend === server) return false;
  const a = bauZeit(laufend);
  const b = bauZeit(server);
  return a !== null && b !== null ? b > a : true;
}

function frisch(m: Versuch | null, jetzt: number): m is Versuch {
  return m !== null && jetzt - m.zeit < VERSUCH_GUELTIG_MS && jetzt >= m.zeit;
}

/** Der Klick: normal neu laden — oder, wenn schon vergeblich versucht, gleich hart. */
export function beiKlick(ziel: string, merker: Versuch | null, jetzt: number): { merker: Versuch; hart: boolean } {
  if (frisch(merker, jetzt) && merker.ziel === ziel && merker.stufe >= 2) {
    return { merker: { ziel, stufe: 3, zeit: jetzt }, hart: true };
  }
  return { merker: { ziel, stufe: 1, zeit: jetzt }, hart: false };
}

/** Nach dem Laden, mit frisch gelesener Server-Fassung (roh) und der geladenen (roh). */
export function beimLaden(
  merker: Versuch | null,
  server: string,
  laufend: string,
  jetzt: number,
): { aktion: NachLaden; merker: Versuch | null } {
  if (!frisch(merker, jetzt)) return { aktion: "nichts", merker: null };
  // Angekommen, oder der Server hat inzwischen eine andere Fassung: kein Fehlschlag dieses Klicks.
  if (!serverIstNeuer(laufend, server) || merker.ziel !== server) return { aktion: "nichts", merker: null };
  if (merker.stufe === 1) return { aktion: "hart", merker: { ...merker, stufe: 2 } };
  if (merker.stufe === 2) return { aktion: "melden", merker: { ...merker, stufe: 3 } };
  return { aktion: "nichts", merker };
}

export const MELDUNG_HAENGT =
  "Die neue Version ließ sich nicht laden. Tippe noch einmal auf „Neue Version“ (lädt dann hart) oder schließe die App ganz und öffne sie neu.";

/** Der Speicher dieser Sitzung — oder null, wenn der Zugriff selbst scheitert (gesperrt, privater Modus). Wirft nie. */
export function holeSpeicher(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

type LeseSpeicher = Pick<Storage, "getItem"> | null;
type SchreibSpeicher = Pick<Storage, "setItem" | "removeItem"> | null;

export function leseMerker(speicher: LeseSpeicher, schluessel: string = VERSUCH_MERKER): Versuch | null {
  try {
    const roh = speicher?.getItem(schluessel);
    if (!roh) return null;
    const j = JSON.parse(roh) as Partial<Versuch>;
    if (typeof j.ziel !== "string" || typeof j.zeit !== "number" || (j.stufe !== 1 && j.stufe !== 2 && j.stufe !== 3)) return null;
    return { ziel: j.ziel, stufe: j.stufe, zeit: j.zeit };
  } catch {
    return null;
  }
}

export function schreibeMerker(speicher: SchreibSpeicher, m: Versuch | null, schluessel: string = VERSUCH_MERKER): void {
  try {
    if (m) speicher?.setItem(schluessel, JSON.stringify(m));
    else speicher?.removeItem(schluessel);
  } catch {
    /* Speicher gesperrt: dann gibt es keinen Schutz, aber auch keine Schleife (nur ein Versuch je Klick) */
  }
}

/** Hartes Neuladen: Worker abmelden, alle Speicher leeren, neu laden. Fehler blockieren das Laden nie. */
export async function hartNeuLaden(env: {
  container?: ServiceWorkerContainer;
  caches?: CacheStorage;
  neuLaden: () => void;
  /** Ruhe? Zuerst gefragt: bei „nein“ wird nichts abgemeldet oder geleert (der Aufrufer versucht es später wieder). */
  darf?: () => boolean;
}): Promise<boolean> {
  if (env.darf && !env.darf()) return false;
  try {
    const rs = (await env.container?.getRegistrations()) ?? [];
    await Promise.all(rs.map((r) => r.unregister()));
  } catch {
    /* weiter */
  }
  try {
    const c = env.caches;
    if (c) await Promise.all((await c.keys()).map((k) => c.delete(k)));
  } catch {
    /* weiter */
  }
  env.neuLaden();
  return true;
}
